import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { Box, Text } from '@doda/io-react/common';
import type {
  JobDetailResponse,
  ScoutJobDetailResponse,
} from '@doda/api-client/aspida/@types';
import { useAppDispatch } from 'redux/hooks';
import { createOptimizedContext } from '@doda/common/src/utils/createOptimizedContext';
import { shouldShowDirectMessageButton } from '@doda/common/src/logics/jobSearchDetail/shouldShowDirectMessageButton';
import { makeCorporateFullName } from '@doda/common/src/logics/jobSearchDetail/makeCorporateFullName';
import { WJOFL_NOEXISTBTN } from '@doda/common/src/constants/properties/const';
import { APPLY_BUTTON_TEXT_VARIANTS } from '@doda/common/src/constants/jobSearchDetail';
import { getApplyButtonAbTestSuffix } from '@doda/common/src/logics/jobSearchDetail/getApplyButtonAbTestSuffix';
import { TagGroup, type TagGroupProps } from '../TagGroup';
import { ApplyButton } from '../ApplyButton';
import { FavoriteButton } from '../FavoriteButton';
import { AskCompanyButton } from '../AskCompanyButton';
import {
  getOfferKindText,
  measureApply,
  measureBookmark,
  measureBookmarkSaved,
} from '../../logics/analytics';
import { useApply } from '../../hooks/useApply';
import type { Tab } from '../../types/tab';
import styles from './index.module.scss';

/**
 * Props
 */
interface FollowHeaderProps {
  corporateName: string;
  occupationName: string;
  tagList: TagGroupProps['tagList'];
  isScout: TagGroupProps['isScout'];
  jobOfferKind: string;
  jid: string;
  directMessage: ScoutJobDetailResponse['directMessage'];
  isApplied: boolean;
  isBookmarked: boolean;
  wapid: string;
  hasDirectLink: boolean;
  directLink: string | undefined;
  initialTab: Tab;
  isCasualInterview?: boolean;
  worschrsvid?: string | null;
  applyButtonChangeAbTestGroupNo?: string;
}

/** スクロールヘッダー */
export const FollowHeader = ({
  corporateName,
  occupationName,
  tagList,
  isScout,
  jobOfferKind,
  jid,
  directMessage,
  isApplied,
  isBookmarked,
  wapid,
  hasDirectLink,
  directLink,
  initialTab,
  isCasualInterview,
  worschrsvid,
  applyButtonChangeAbTestGroupNo,
}: FollowHeaderProps) => {
  const dispatch = useAppDispatch();
  const showHeader = useDisplayState();
  const isDirect = jobOfferKind === '2' || jobOfferKind === '3';
  const canSend = Boolean(directMessage?.isCanSent);
  const showAskButton = shouldShowDirectMessageButton(isDirect, canSend);
  // 応募済みまたはカジュアル面談の場合、気になるボタン非表示
  const showFavoriteButton = !isApplied && !isCasualInterview;
  const showApplyButton = jobOfferKind !== WJOFL_NOEXISTBTN;
  const { apply } = useApply({
    jid,
    jobOfferKind,
    isScout,
    initialTab,
    hasDirectLink,
    directLink,
    position: 'top',
    isCasualInterview,
    worschrsvid,
  });
  const abTestSuffix = getApplyButtonAbTestSuffix(
    applyButtonChangeAbTestGroupNo ?? '0'
  );
  const isApplyClickedRef = useRef(false);
  const handleApply = () => {
    if (isApplyClickedRef.current) return;
    isApplyClickedRef.current = true;
    dispatch(
      measureApply({
        position: 'Top',
        apply: getOfferKindText(jobOfferKind),
        initialTab,
        jid,
        abTestSuffix,
      })
    );
    apply();
  };
  const handleBookmarkSaved = () => {
    dispatch(
      measureBookmarkSaved({
        position: 'Top',
        initialTab,
        jid,
      })
    );
  };

  const handleBookmark = () => {
    dispatch(
      measureBookmark({
        initialTab,
        jid,
      })
    );
  };

  const variantKey = (applyButtonChangeAbTestGroupNo ||
    '0') as keyof typeof APPLY_BUTTON_TEXT_VARIANTS;
  const balloonText =
    variantKey in APPLY_BUTTON_TEXT_VARIANTS
      ? APPLY_BUTTON_TEXT_VARIANTS[variantKey].balloonText
      : null;
  // DODASITE-25064 応募ボタンUI変更ABテスト
  // テストパターン1と0の場合は応募ボタン幅は 150px
  // テストパターン2、3で直接応募の場合の応募ボタン幅は 194px 、エージェント応募は 222px
  const agentApply = jobOfferKind === '0';
  const isNotAbTargetOrOriginal =
    applyButtonChangeAbTestGroupNo === '0' ||
    applyButtonChangeAbTestGroupNo === '1';
  const applyButtonWidth =
    (isNotAbTargetOrOriginal && 150) || (agentApply && 222) || 194;

  return (
    <Box
      color="white"
      boxShadow="black"
      standardPadding
      className={classNames(styles['jobSearchDetail-followHeaderContainer'], {
        [styles['jobSearchDetail-followHeaderContainer--show']]: showHeader,
      })}
    >
      <div className={styles['jobSearchDetail-followHeader']}>
        <div className={styles['jobSearchDetail-followHeader__info']}>
          <Text
            color="black_pc"
            size="14--articlePC"
            weight="bold"
            as="p"
            className={styles['jobSearchDetail-followHeader__text']}
          >
            {corporateName}
          </Text>
          <Text
            color="gray"
            size="12"
            weight="normal"
            as="p"
            className={styles['jobSearchDetail-followHeader__text']}
            style={{ marginBlockEnd: 6 }}
          >
            {occupationName}
          </Text>
          <TagGroup tagList={tagList} isScout={isScout} />
        </div>
        <div className={styles['jobSearchDetail-followHeader__buttonGroup']}>
          {showApplyButton && (
            <div
              className={classNames({
                // DODASITE-25064 応募ボタンUI変更ABテスト テストパターン2、3にのみクラス付与
                [styles['jobSearchDetail-followHeader__applyButtonWrapper']]:
                  applyButtonChangeAbTestGroupNo === '2' ||
                  applyButtonChangeAbTestGroupNo === '3',
              })}
            >
              {balloonText && (
                <Text
                  as="span"
                  color="black_pc"
                  size="10"
                  weight="bold"
                  className={styles['jobSearchDetail-followHeader__balloon']}
                >
                  {balloonText}
                </Text>
              )}
              <ApplyButton
                size="S"
                width={applyButtonWidth}
                jobOfferKind={jobOfferKind}
                onClick={handleApply}
                abTestGroupNo={applyButtonChangeAbTestGroupNo}
              />
            </div>
          )}
          {showAskButton && (
            <AskCompanyButton
              jid={jid}
              size="S"
              width={150}
              isReachedLimit={Boolean(directMessage?.isReachedLimit)}
              isApplied={isApplied}
              onClick={() => {}}
            />
          )}
          {showFavoriteButton && (
            <FavoriteButton
              isBookmarked={isBookmarked}
              jid={jid}
              wapid={wapid}
              bookmarkListUrl="/dcfront/bookmark/bookmarkList/"
              size="S"
              width="150px"
              onClickSaved={handleBookmarkSaved}
              onClickFavorite={handleBookmark}
            />
          )}
        </div>
      </div>
    </Box>
  );
};

/**
 * props生成
 */
export function getFollowHeaderProps(
  job: JobDetailResponse | ScoutJobDetailResponse,
  isScout: boolean,
  initialTab: Tab,
  applyButtonChangeAbTestGroupNo?: string,
  isCasualInterview?: boolean,
  worschrsvid?: string | null
): FollowHeaderProps {
  const jobDetail = job.job || {};
  return {
    corporateName: makeCorporateFullName(jobDetail),
    occupationName: jobDetail.occupationName || '',
    tagList: jobDetail.kodawariIconList || [],
    isScout,
    jobOfferKind: jobDetail.jobOfferKind || '',
    jid: jobDetail.jid || '',
    directMessage: 'directMessage' in job ? job.directMessage : undefined,
    isApplied: Boolean(job.apply?.isApplied),
    isBookmarked: Boolean(job.isBookmarkSaved),
    wapid: jobDetail.wapid || '',
    hasDirectLink: Boolean(jobDetail.hasDirectLink),
    directLink: jobDetail.directLink || undefined,
    initialTab,
    applyButtonChangeAbTestGroupNo,
    isCasualInterview,
    worschrsvid,
  };
}

const [FollowHeaderProvider, useDisplayState, useSetDisplay] =
  createOptimizedContext(false);

export { FollowHeaderProvider, useDisplayState };

/**
 * FollowHeaderDisplayAreaProps
 */
interface FollowHeaderDisplayAreaProps {
  /** コンポーネントを表示するエリア */
  children: React.ReactNode;
}

/** スクロールヘッダー表示エリア指定用コンポーネント */
export const FollowHeaderDisplayArea = ({
  children,
}: FollowHeaderDisplayAreaProps) => {
  const setShow = useSetDisplay();
  const topRef = useRef<HTMLSpanElement>(null);
  const bottomRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!topRef.current || !bottomRef.current) return;
    const topElement = topRef.current;
    const bottomElement = bottomRef.current;

    const callback = () => {
      const topOffset = topElement.getBoundingClientRect().top;
      const bottomOffset = bottomElement.getBoundingClientRect().top;
      // 下端より下へスクロール
      if (bottomOffset < 0) setShow(false);
      // 上端より下へスクロール(下端より上側=エリア内)
      else if (topOffset < 0) setShow(true);
      // その他
      else setShow(false);
    };
    window.addEventListener('scroll', callback);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('scroll', callback);
    };
  }, [setShow]);

  return (
    <>
      <span ref={topRef} />
      {children}
      <span ref={bottomRef} />
    </>
  );
};
